import React, { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import styles from './CustomizationPreview.module.css'
import {
    addCanvasToWrapper,
    changeBackground,
    resizeEverything,
} from '../../../../helpers/resizeEverything'
import { Customization } from '../../../../types/Customization'
import { StoredTemplate } from '../../../../types/Individualization'
import { useFetchData } from '../../../../hooks/useFetchData'
import { Font } from '../../../../types/Font'
import { ApiRoutes } from '../../../../services/ApiService'
import { loadInputs } from '../../../../helpers/fulfillment'
import Sidebar from '../../../Individualization/Progress/DefineAreas/Sidebar'

const CustomizationPreview: React.FC<{
    activeTemplate: StoredTemplate | null
    setActiveTemplate: React.Dispatch<
        React.SetStateAction<StoredTemplate | null>
    >
    customization: Customization
    templates: StoredTemplate[]
}> = ({ activeTemplate, setActiveTemplate, customization, templates }) => {
    const wrapper = useRef<HTMLDivElement | null>(null)
    const resizeObserver = useRef<ResizeObserver | null>(null)
    const timer = useRef<undefined | number | NodeJS.Timeout>(undefined)

    const { data: fonts, fetch: fetchFonts } = useFetchData<Font>(
        ApiRoutes.FONTS
    )

    const [showTemplatesSidebar, setShowTemplatesSidebar] = useState<boolean>(
        templates.length > 1
    )

    const toggleTemplatesSidebar = () => {
        setShowTemplatesSidebar((prev) => !prev)
    }

    const changePage = (template_id: string | number) => {
        const template = templates.find((t) => t.id === template_id) || null
        setActiveTemplate(template)
    }

    useEffect(() => {
        fonts
            .filter((font) => {
                return (
                    font.base64?.length && font.base64.startsWith('data:font')
                )
            })
            .forEach((font) => {
                new FontFace(font.name, `url(${font.base64})`)
                    .load()
                    .then((f) => {
                        document.fonts.add(f)
                    })
            })
    }, [fonts])

    useEffect(() => {
        fetchFonts()
    }, [fetchFonts])

    useEffect(() => {
        if (!wrapper.current || !templates.length) return

        resizeObserver.current = new ResizeObserver(() => {
            clearTimeout(timer.current)
            timer.current = setTimeout(async () => {
                templates.forEach((template) => {
                    if (template.canvas && wrapper.current) {
                        resizeEverything(template.canvas, wrapper.current, 1000)
                    }
                })
            }, 500)
        })
        resizeObserver.current.observe(wrapper.current)

        return () => {
            if (resizeObserver.current) {
                resizeObserver.current.disconnect()
                resizeObserver.current = null
            }
            if (timer.current) {
                clearTimeout(timer.current)
                timer.current = undefined
            }
        }
    }, [templates])

    // Clear the canvas and load the active template and inputs when the active template or customization changes
    useEffect(() => {
        if (activeTemplate && activeTemplate.canvas && wrapper.current) {
            addCanvasToWrapper(activeTemplate.canvas, wrapper.current, true)
            activeTemplate.canvas.clear()
            changeBackground(activeTemplate, activeTemplate.canvas)
            loadInputs(
                customization,
                activeTemplate,
                activeTemplate.canvas
            ).then(() => {
                activeTemplate?.canvas?.renderAll()
            })
        }
    }, [activeTemplate])

    return (
        <div className={'zr-position-relative'} style={{ overflow: 'hidden' }}>
            <Sidebar
                templates={templates}
                changeTemplate={changePage}
                active={activeTemplate?.id ?? null}
                show={showTemplatesSidebar}
                toggleSidebar={toggleTemplatesSidebar}
                colors={{ background: '#FC0', text: '#000' }}
            />
            <div
                className={cx(styles['customization-preview'])}
                ref={wrapper}
            ></div>
        </div>
    )
}

export default CustomizationPreview
